import React from "react";

import { SegmentedControl } from "@/components/ui/segmented-control";
import type { SegmentedControlOption } from "@/components/ui/segmented-control";
import type { TimelineDateBasis } from "@/stores/documents/repository";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface TimelineDateBasisControlProps {
  value: TimelineDateBasis;
  onValueChange: (basis: TimelineDateBasis) => void;
  /** The database cannot place by upload date yet: Upload is shown disabled. */
  uploadUnavailable?: boolean;
}

/**
 * "Date: [Upload | Creation]" — which date places documents on the Timeline.
 * Upload is the platform upload date (`created_at`); Creation is the date in the
 * file's own metadata (`effective_modified_at`), the Timeline's placement before
 * this control existed.
 */
export const TimelineDateBasisControl = React.memo(function TimelineDateBasisControl({
  value,
  onValueChange,
  uploadUnavailable = false,
}: TimelineDateBasisControlProps) {
  const { t } = useLanguage();
  const copy = t.timeline.dateBasis;
  const options: SegmentedControlOption<TimelineDateBasis>[] = [
    {
      value: "upload",
      label: copy.upload,
      disabled: uploadUnavailable,
      tooltip: uploadUnavailable ? copy.uploadUnavailableTooltip : copy.uploadTooltip,
    },
    { value: "file", label: copy.creation, tooltip: copy.creationTooltip },
  ];

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{copy.label}:</span>
      <SegmentedControl
        value={value}
        onValueChange={onValueChange}
        options={options}
        aria-label={copy.label}
      />
    </div>
  );
});
